'use client';

// Forum image attachments — Reddit-style: images upload to OUR storage
// (never hotlinked from third-party hosts), get embedded in thread/comment
// bodies as ![image](url) markdown tokens, and render inline with a
// click-to-expand lightbox. Only URLs from our forum-images bucket render
// as images; anything else stays plain text.

import { useCallback, useRef, useState } from 'react';
import { ImagePlus, X, Loader2 } from 'lucide-react';
import { useLang } from '@/lib/i18n/LanguageContext';
import MentionTextarea from './MentionTextarea';

export const MAX_IMAGES = 4;

const BUCKET_PREFIX = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/forum-images/`;
const TOKEN_RE = /!\[[^\]\n]*\]\((https?:\/\/[^\s)]+)\)/g;

// ── Body ⇄ tokens ──────────────────────────────────────────────────────────

export function appendImageTokens(body: string, urls: string[]): string {
  if (urls.length === 0) return body;
  const tokens = urls.map((u) => `![image](${u})`).join('\n');
  return body ? `${body}\n\n${tokens}` : tokens;
}

// First attached image, for Reddit-style thumbnails in the thread list
export function firstImage(body: string | null): string | null {
  if (!body) return null;
  for (const m of body.matchAll(TOKEN_RE)) {
    if (m[1].startsWith(BUCKET_PREFIX)) return m[1];
  }
  return null;
}

// Split a stored body into editable text + its attached images, so the edit
// form shows clean text and a thumbnail strip instead of raw ![image](…) tokens.
export function splitBody(body: string | null): { text: string; images: string[] } {
  if (!body) return { text: '', images: [] };
  const images: string[] = [];
  const text = body
    .replace(TOKEN_RE, (match, url) => {
      if (url.startsWith(BUCKET_PREFIX)) { images.push(url); return ''; }
      return match; // foreign URLs are plain text — leave them alone
    })
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text, images };
}

type Segment = { type: 'text'; value: string } | { type: 'image'; value: string };

export function parseBody(body: string): Segment[] {
  const segments: Segment[] = [];
  let last = 0;
  for (const m of body.matchAll(TOKEN_RE)) {
    const url = m[1];
    // Only our own bucket renders as an image — foreign URLs stay as text
    if (!url.startsWith(BUCKET_PREFIX)) continue;
    if (m.index! > last) segments.push({ type: 'text', value: body.slice(last, m.index) });
    segments.push({ type: 'image', value: url });
    last = m.index! + m[0].length;
  }
  if (last < body.length) segments.push({ type: 'text', value: body.slice(last) });
  return segments;
}

// ── Inline body renderer + lightbox ────────────────────────────────────────

const URL_RE = /(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)])/g;
const MENTION_RE = /@([A-Za-z0-9_.]{2,30})/g;

// @username → profile link (mentions also notify via DB trigger)
function Mentions({ text }: { text: string }) {
  const parts = text.split(MENTION_RE);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <a key={i} href={`/profile/${encodeURIComponent(part)}`} style={{ color: 'var(--accent)', fontWeight: 600 }}>
            @{part}
          </a>
        ) : (
          part
        )
      )}
    </>
  );
}

// Plain text with clickable links (safe: rel keeps referrer/SEO abuse out)
function Linkified({ text }: { text: string }) {
  const parts = text.split(URL_RE);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer nofollow"
            style={{ color: 'var(--info)', wordBreak: 'break-all' }}
          >
            {part}
          </a>
        ) : (
          <Mentions key={i} text={part} />
        )
      )}
    </>
  );
}

export function BodyContent({ body, className, style }: {
  body: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const { t } = useLang();
  const [lightbox, setLightbox] = useState<string | null>(null);
  const segments = parseBody(body);

  return (
    <>
      {segments.map((seg, i) =>
        seg.type === 'text' ? (
          seg.value.trim() ? (
            <p key={i} className={className} style={style}><Linkified text={seg.value.replace(/^\n+|\n+$/g, '')} /></p>
          ) : null
        ) : (
          <img
            key={i}
            src={seg.value}
            alt={t('Attached image')}
            loading="lazy"
            onClick={() => setLightbox(seg.value)}
            className="rounded-lg my-2 cursor-zoom-in"
            style={{ maxWidth: '100%', maxHeight: 480, border: '1px solid var(--border)' }}
          />
        )
      )}
      {lightbox && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 cursor-zoom-out"
          style={{ background: 'rgba(0,0,0,0.88)' }}
          onClick={() => setLightbox(null)}
        >
          <button
            aria-label={t('Close')}
            className="absolute top-4 right-4 p-2 rounded-full"
            style={{ background: 'rgba(255,255,255,0.1)', color: '#e8ecf0' }}
            onClick={() => setLightbox(null)}
          >
            <X size={20} />
          </button>
          <img
            src={lightbox}
            alt={t('Attached image')}
            className="rounded-lg"
            style={{ maxWidth: '95vw', maxHeight: '92vh', objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

// ── Upload (with client-side downscale, Reddit re-encodes server-side) ─────

async function compressImage(file: File): Promise<Blob> {
  // GIF: canvas would kill the animation; small files: not worth re-encoding
  if (file.type === 'image/gif' || file.size < 500 * 1024) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const MAX_DIM = 1920;
    const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/webp', 0.85)
    );
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

async function uploadOne(file: File, accessToken: string | null): Promise<string> {
  const blob = await compressImage(file);
  const fd = new FormData();
  const name = blob === file ? file.name : file.name.replace(/\.\w+$/, '') + '.webp';
  fd.append('file', new File([blob], name, { type: blob.type || file.type }));
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const res = await fetch('/api/forum/upload-image', { method: 'POST', headers, body: fd });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || 'Upload failed');
  return d.url as string;
}

export function useImageAttachments(accessToken: string | null) {
  const { t } = useLang();
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ref mirrors state so two rapid addFiles calls (paste + button) respect the cap
  const countRef = useRef(0);

  const addFiles = useCallback(async (files: File[] | FileList) => {
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (list.length === 0) return;
    if (countRef.current + list.length > MAX_IMAGES) {
      setError(t('You can attach up to 4 images'));
      return;
    }
    countRef.current += list.length;
    setUploading(true);
    setError(null);
    try {
      for (const file of list) {
        const url = await uploadOne(file, accessToken);
        setImages((prev) => [...prev, url]);
      }
    } catch (e: any) {
      setError(e?.message || t('Image upload failed'));
      // Resync the cap counter with what actually made it up
      setImages((prev) => { countRef.current = prev.length; return prev; });
    } finally {
      setUploading(false);
    }
  }, [accessToken, t]);

  const remove = useCallback((url: string) => {
    setImages((prev) => {
      const next = prev.filter((u) => u !== url);
      countRef.current = next.length;
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setImages([]);
    countRef.current = 0;
    setError(null);
  }, []);

  // Preload existing images (edit mode)
  const load = useCallback((urls: string[]) => {
    const next = urls.slice(0, MAX_IMAGES);
    setImages(next);
    countRef.current = next.length;
    setError(null);
  }, []);

  // Paste/drop handlers to wire onto a textarea
  const onPaste = useCallback((e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);

  const onDrop = useCallback((e: React.DragEvent) => {
    const files = Array.from(e.dataTransfer?.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (Array.from(e.dataTransfer?.items ?? []).some((i) => i.kind === 'file')) e.preventDefault();
  }, []);

  return { images, uploading, error, addFiles, remove, reset, load, onPaste, onDrop, onDragOver };
}

// ── Inline edit form: textarea + image picker, preloaded from a stored body ─

export function BodyEditor({ initialBody, accessToken, onCancel, onSubmit, submitLabel, rows = 4, allowEmptyText = true }: {
  initialBody: string | null;
  accessToken: string | null;
  onCancel: () => void;
  /** Returns an error message to display, or null on success. */
  onSubmit: (body: string) => Promise<string | null>;
  submitLabel: string;
  rows?: number;
  /** Threads may be image-only or empty; comments must not end up empty. */
  allowEmptyText?: boolean;
}) {
  const { t } = useLang();
  const initial = splitBody(initialBody);
  const [text, setText] = useState(initial.text);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attach = useImageAttachments(accessToken);
  const loadedRef = useRef(false);
  if (!loadedRef.current) {
    loadedRef.current = true;
    // Safe during first render: state setters before mount don't re-render loops
    attach.load(initial.images);
  }

  const empty = !text.trim() && attach.images.length === 0;

  async function handleSave() {
    if (saving || attach.uploading) return;
    if (!allowEmptyText && empty) return;
    setSaving(true);
    setError(null);
    const body = appendImageTokens(text.trim(), attach.images);
    const err = await onSubmit(body);
    setSaving(false);
    if (err) setError(err);
  }

  return (
    <div className="mt-2">
      <MentionTextarea
        value={text}
        onValueChange={setText}
        rows={rows}
        autoFocus
        className="input-base w-full px-3 py-2 text-sm resize-y"
        onPaste={attach.onPaste}
        onDrop={attach.onDrop}
        onDragOver={attach.onDragOver}
      />
      <ImagePicker attach={attach} />
      {error && <p className="text-xs mt-1.5" style={{ color: 'var(--bearish)' }}>{error}</p>}
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={onCancel} className="btn-ghost px-3 py-1.5 text-xs">{t('Cancel')}</button>
        <button
          onClick={handleSave}
          disabled={saving || attach.uploading || (!allowEmptyText && empty)}
          className="btn-primary px-3 py-1.5 text-xs"
          style={{ opacity: saving || attach.uploading || (!allowEmptyText && empty) ? 0.6 : 1 }}
        >
          {saving ? t('Saving…') : submitLabel}
        </button>
      </div>
    </div>
  );
}

// ── Picker UI: add-image button + thumbnail strip ──────────────────────────

export function ImagePicker({ attach, disabled }: {
  attach: ReturnType<typeof useImageAttachments>;
  disabled?: boolean;
}) {
  const { t } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  const { images, uploading, error, addFiles, remove } = attach;

  return (
    <div>
      {images.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-2">
          {images.map((url) => (
            <div key={url} className="relative">
              <img
                src={url}
                alt={t('Attached image')}
                className="rounded-lg object-cover"
                style={{ width: 64, height: 64, border: '1px solid var(--border)' }}
              />
              <button
                onClick={() => remove(url)}
                aria-label={t('Remove image')}
                className="absolute -top-1.5 -right-1.5 rounded-full p-0.5"
                style={{ background: 'var(--inverse-bg)', border: '1px solid var(--inverse-bg)', color: 'var(--inverse-text)' }}
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading || images.length >= MAX_IMAGES}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
          style={{
            background: 'var(--bg-sunken)',
            border: '1px solid var(--border)',
            color: 'var(--text-secondary)',
            opacity: disabled || uploading || images.length >= MAX_IMAGES ? 0.5 : 1,
          }}
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
          {uploading ? t('Uploading image…') : t('Add image')}
        </button>
        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
          {t('Up to 4 images (JPG, PNG, WebP, GIF, max 10MB)')}
        </span>
      </div>

      {error && <p className="text-xs mt-1.5" style={{ color: 'var(--bearish)' }}>{error}</p>}
    </div>
  );
}
